// No dependency for .xlsx: the only `xlsx` package on npm carries a
// prototype-pollution advisory, and this bundle ships inside the mobile apps.

/**
 * A sheet's rows in its own order, blank ones included, so a row's position
 * is the row number the operator sees in the spreadsheet.
 */
export type Grid = string[][];

/** A row whose cells are all empty or only spaces. */
export const isBlankRow = (row: readonly string[]) => !row.some(cell => cell.trim());

export type SpreadsheetErrorCode = 'unsupportedFile' | 'tooLarge' | 'unreadable' | 'xlsxUnsupported';

export class SpreadsheetError extends Error {
    readonly code: SpreadsheetErrorCode;

    constructor(code: SpreadsheetErrorCode) {
        super(code);
        this.name = 'SpreadsheetError';
        this.code = code;
    }
}

const TEXT_EXTENSIONS = /\.(csv|tsv|txt)$/i;
const XLSX_EXTENSION = /\.xlsx$/i;

/**
 * Far above a thousand rows of text, which weigh a few megabytes. A larger
 * file is more likely the wrong one, and reading it whole would stall a phone.
 */
export const MAX_SPREADSHEET_BYTES = 10 * 1024 * 1024;

/**
 * The first sheet's rows, blank ones included. Throws a SpreadsheetError for a
 * file of another kind, too large or unreadable. The format is checked before
 * the size, so the wrong kind of file is not answered with advice to split it.
 */
export async function readSpreadsheetFile(file: File): Promise<Grid> {
    const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    const isXlsx = XLSX_EXTENSION.test(file.name) || startsWithZipSignature(head);
    if (!isXlsx && !TEXT_EXTENSIONS.test(file.name) && !file.type.startsWith('text/')) {
        throw new SpreadsheetError('unsupportedFile');
    }
    if (file.size > MAX_SPREADSHEET_BYTES) throw new SpreadsheetError('tooLarge');

    const bytes = new Uint8Array(await file.arrayBuffer());

    return isXlsx ? readXlsx(bytes) : parseDelimited(decodeText(bytes));
}

/**
 * Excel saves a CSV in the system's legacy code page on Spanish and Portuguese
 * Windows, so bytes that are not UTF-8 are read as Windows-1252 rather than
 * shown with broken accents.
 */
export function decodeText(bytes: Uint8Array): string {
    // Excel's "Unicode Text" export is UTF-16 behind its byte order mark.
    if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes);
    if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes);

    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
        return new TextDecoder('windows-1252').decode(bytes);
    }
}

/** Splits pasted or exported text into cells, honouring quotes so a cell can hold separators and newlines. */
export function parseDelimited(input: string): Grid {
    // Excel and Sheets exports start with a BOM, which would hide the first header.
    const text = (input ?? '').replace(/^\ufeff/, '');
    const delimiter = detectDelimiter(text);
    const rows: Grid = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;

    const endRow = () => {
        row.push(cell);
        cell = '';
        rows.push(row);
        row = [];
    };

    for (let index = 0; index < text.length; index++) {
        const char = text[index];

        if (quoted) {
            if (char !== '"') {
                cell += char;
            } else if (text[index + 1] === '"') {
                cell += '"';
                index++;
            } else {
                quoted = false;
            }
            continue;
        }

        if (char === '"' && cell === '' && opensQuotedCell(text, index, delimiter)) {
            quoted = true;
        } else if (char === delimiter) {
            row.push(cell);
            cell = '';
        } else if (char === '\n' || char === '\r') {
            if (char === '\r' && text[index + 1] === '\n') index++;
            endRow();
        } else {
            cell += char;
        }
    }

    endRow();

    return rows;
}

/**
 * Sheets and Excel quote a pasted cell only when it holds a line break, so a
 * title that starts with a quoted work arrives bare. A quote opens a cell
 * only when its closing quote ends the cell.
 */
function opensQuotedCell(text: string, start: number, delimiter: string): boolean {
    for (let index = start + 1; index < text.length; index++) {
        if (text[index] !== '"') continue;
        if (text[index + 1] === '"') {
            index++;
            continue;
        }

        const after = text[index + 1];
        return after === undefined || after === delimiter || after === '\n' || after === '\r';
    }

    return false;
}

/**
 * Sheets and Excel put tabs on the clipboard, so a tab anywhere decides it.
 * Otherwise the separator that splits every line the same way wins: a comma
 * inside one prose cell must not outvote the semicolons a European Excel writes.
 */
function detectDelimiter(text: string): string {
    const lines = text
        .split(/\r?\n/)
        .filter(line => line.trim())
        .slice(0, 5);

    if (lines.some(line => line.includes('\t'))) return '\t';

    const scored = [';', ','].map(candidate => {
        const counts = lines.map(line => line.split(candidate).length - 1);
        return {
            candidate,
            count: counts[0] ?? 0,
            consistent: counts.every(count => count === counts[0]),
        };
    });

    const best = scored
        .filter(score => score.count > 0)
        .sort((a, b) => Number(b.consistent) - Number(a.consistent) || b.count - a.count)[0];

    return best ? best.candidate : ',';
}

interface ZipEntry {
    method: number;
    compressedSize: number;
    localOffset: number;
}

/** WebViews older than iOS 16.4 cannot inflate a ZIP natively. A CSV or a paste still works there. */
function canReadXlsx(): boolean {
    try {
        new DecompressionStream('deflate-raw' as CompressionFormat);
        return true;
    } catch {
        return false;
    }
}

function startsWithZipSignature(bytes: Uint8Array): boolean {
    return bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

async function readXlsx(bytes: Uint8Array): Promise<Grid> {
    const entries = readZipEntries(bytes);

    const sharedStrings = await partOf(entries, bytes, 'xl/sharedStrings.xml');
    const strings = sharedStrings ? childrenNamed(sharedStrings.documentElement, 'si').map(textOf) : [];
    const sheetPath = await firstSheetPath(entries, bytes);
    const sheet = sheetPath ? await partOf(entries, bytes, sheetPath) : null;
    if (!sheet) throw new SpreadsheetError('unreadable');

    return parseSheet(sheet, strings);
}

function readZipEntries(bytes: Uint8Array): Map<string, ZipEntry> {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

    // The end of the central directory, searched from the back past any trailing comment.
    let end = -1;
    const floor = Math.max(0, bytes.length - 65558);
    for (let index = bytes.length - 22; index >= floor; index--) {
        if (view.getUint32(index, true) === 0x06054b50) {
            end = index;
            break;
        }
    }
    if (end < 0) throw new SpreadsheetError('unreadable');

    const total = view.getUint16(end + 10, true);
    let offset = view.getUint32(end + 16, true);
    const entries = new Map<string, ZipEntry>();
    const decoder = new TextDecoder();

    for (let index = 0; index < total; index++) {
        if (offset + 46 > bytes.length || view.getUint32(offset, true) !== 0x02014b50) break;

        const nameLength = view.getUint16(offset + 28, true);
        const extraLength = view.getUint16(offset + 30, true);
        const commentLength = view.getUint16(offset + 32, true);
        const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength));

        entries.set(name, {
            method: view.getUint16(offset + 10, true),
            compressedSize: view.getUint32(offset + 20, true),
            localOffset: view.getUint32(offset + 42, true),
        });
        offset += 46 + nameLength + extraLength + commentLength;
    }

    return entries;
}

async function entryText(entries: Map<string, ZipEntry>, bytes: Uint8Array, path: string): Promise<string> {
    const entry = entries.get(path);
    if (!entry) return '';

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const nameLength = view.getUint16(entry.localOffset + 26, true);
    const extraLength = view.getUint16(entry.localOffset + 28, true);
    const start = entry.localOffset + 30 + nameLength + extraLength;
    const data = bytes.subarray(start, start + entry.compressedSize);

    if (entry.method === 0) return new TextDecoder().decode(data);
    if (entry.method !== 8) throw new SpreadsheetError('unsupportedFile');
    if (!canReadXlsx()) throw new SpreadsheetError('xlsxUnsupported');

    const inflated = new Response(data).body!.pipeThrough(new DecompressionStream('deflate-raw' as CompressionFormat));
    return new Response(inflated).text();
}

/** A part of the workbook as a document, or null when the file has none. */
async function partOf(entries: Map<string, ZipEntry>, bytes: Uint8Array, path: string): Promise<Document | null> {
    const xml = await entryText(entries, bytes, path);
    if (!xml) return null;

    const document = new DOMParser().parseFromString(xml, 'application/xml');
    // A part that is not well-formed parses into an error page, not an exception.
    if (document.getElementsByTagName('parsererror').length > 0) throw new SpreadsheetError('unreadable');

    return document;
}

// By local name, whatever prefix the writer chose: some Open XML SDK exports write `<x:row>`.
const descendantsNamed = (node: Document | Element, name: string) => [...node.getElementsByTagNameNS('*', name)];

const childrenNamed = (element: Element, name: string) =>
    [...element.children].filter(child => child.localName === name);

/**
 * The tab the workbook lists first, which is the one an operator fills in:
 * the parts are numbered in the order the tabs were made, not as they stand.
 */
async function firstSheetPath(entries: Map<string, ZipEntry>, bytes: Uint8Array): Promise<string | null> {
    const workbook = await partOf(entries, bytes, 'xl/workbook.xml');
    const [firstSheet] = workbook ? descendantsNamed(workbook, 'sheet') : [];
    // The relationship id is the one prefixed attribute named id, `r:id` in Excel's files.
    const id = [...(firstSheet?.attributes ?? [])].find(
        attribute => attribute.localName === 'id' && attribute.prefix
    )?.value;

    if (id) {
        const relations = await partOf(entries, bytes, 'xl/_rels/workbook.xml.rels');
        const relation =
            relations && descendantsNamed(relations, 'Relationship').find(node => node.getAttribute('Id') === id);
        const target = relation?.getAttribute('Target');
        const path = target && (target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`);
        if (path && entries.has(path)) return path;
    }

    return lowestNumberedSheetPath(entries);
}

function lowestNumberedSheetPath(entries: Map<string, ZipEntry>): string | null {
    const sheetNumber = (path: string) => parseInt(path.match(/sheet(\d+)\.xml$/)?.[1] ?? '0', 10);
    const sheets = [...entries.keys()]
        .filter(name => /^xl\/worksheets\/sheet\d+\.xml$/.test(name))
        .sort((a, b) => sheetNumber(a) - sheetNumber(b));

    return sheets[0] ?? null;
}

/** A rich cell is split across runs: its text is all of them joined, without the phonetic guides. */
function textOf(element: Element): string {
    return descendantsNamed(element, 't')
        .filter(text => text.parentElement?.localName !== 'rPh')
        .map(text => decodeCellText(text.textContent ?? ''))
        .join('');
}

function parseSheet(sheet: Document, strings: string[]): Grid {
    const rows: Grid = [];

    for (const row of descendantsNamed(sheet, 'row')) {
        const cells: string[] = [];
        let next = 0;

        for (const cell of childrenNamed(row, 'c')) {
            const reference = cell.getAttribute('r')?.match(/^[A-Z]+/)?.[0];
            const column = reference ? columnIndex(reference) : next;
            cells[column] = cellValue(cell, strings);
            next = column + 1;
        }

        // A sheet stores no element for an empty row, so its number places the row.
        const number = parseInt(row.getAttribute('r') ?? '', 10);
        const index = number > 0 ? number - 1 : rows.length;
        while (rows.length < index) rows.push([]);
        // Empty cells come back as holes, and the plan reads cells by position.
        rows[index] = Array.from({ length: cells.length }, (_, column) => cells[column] ?? '');
    }

    return rows;
}

function cellValue(cell: Element, strings: string[]): string {
    const type = cell.getAttribute('t') ?? 'n';
    const value = childrenNamed(cell, 'v')[0]?.textContent ?? '';

    if (type === 's') return strings[parseInt(value, 10)] ?? '';
    if (type === 'inlineStr') return textOf(cell);

    return decodeCellText(value);
}

/** "A" → 0, "B" → 1, "AA" → 26 */
function columnIndex(letters: string): number {
    return [...letters].reduce((total, letter) => total * 26 + (letter.charCodeAt(0) - 64), 0) - 1;
}

/**
 * Excel writes the characters XML cannot carry as `_xHHHH_`, the carriage
 * return of a cell pasted from Word among them, and `_x005F_` for a literal
 * underscore. The carriage returns go as plain line breaks, like a CSV's.
 */
function decodeCellText(text: string): string {
    return text
        .replace(/_x([0-9A-Fa-f]{4})_/g, (_match, code) => String.fromCharCode(parseInt(code, 16)))
        .replace(/\r\n?/g, '\n');
}
